import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Badge } from '@components/atoms/badge';
import { Button } from '@components/atoms/button';
import { Icon } from '@components/atoms/icon';
import { Typography } from '@components/atoms/typography';
import { ErrorMarkIcon } from '@components/icons/error-mark-icon';
import { MiniArrowIcon } from '@components/icons/mini-arrow-icon';
import { LanguageSelector } from '@components/organisms/language-selector';
import { SteppedTabs } from '@components/organisms/stepped-tabs';
import { useLogger } from '@hooks/use-logger';
import {
    BadgeStatusEnum,
    DocsVerificationStepEnum,
    DocsVerificationTabEnum,
    OrganizationRoleEnum,
} from '@models/enums';
import { DocsVerificationFileUploadApproachEnum } from '@models/enums/docs-verification/docs-verification-file-upload-approach-enum';
import type { ThemeProps } from '@models/types';
import { DemoDataButton } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/demo-data-button';
import { DocumentsStepEnum } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/tabs/documents/types';
import { PersonalData } from '@modules/dashboard/pages/kyc/docs-verification/docs-verification-page/tabs/personal-data';
import { useProceedVerificationSDKMutation } from '@modules/docs-verification-widget/api';
import { useTheme } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { RootState } from '@store/root-reducer';
import { docsVerificationSlice } from '@store/slices/docs-verification/docs-verification-slice';
import { getEnvVar } from '@utils/get-env-var';

import { Timer } from '../docs-verification-steps/start/timer';
import { usePageTitle } from '../hooks/use-page-title';
import { useSelectedChecks } from '../hooks/use-selected-checks';

import { Address } from './address';
import { STEPS_MOBILE_TITLE } from './constants';
import { Documents } from './documents';
import { Selfie } from './selfie';
import { TabContainer } from './styles';

// eslint-disable-next-line complexity
export const VerificationStep = () => {
    const { t } = useTranslation();
    const theme = useTheme<ThemeProps>();
    const matchesMobileSm = useMediaQuery(`(max-width:${theme.breakPoints.sm})`);
    const dispatch = useDispatch();
    const [proceedVerificationSDK] = useProceedVerificationSDKMutation();
    const sdkOptions = useSelector((state: RootState) => state.docsVerification.sdkOptions);
    const isCameraMode = useSelector((state: RootState) => state.docsVerification.isCameraMode);
    const isShowRequirements = useSelector(
        (state: RootState) => state.docsVerification.isShowRequirements,
    );
    const currentTab = useSelector((state: RootState) => state.docsVerification.currentTab);
    const documentsFiles = useSelector(
        (state: RootState) => state.docsVerification.documents.files,
    ).filter((item) => item.tab === DocsVerificationTabEnum.DOCUMENT);
    const documentsStep = useSelector((state: RootState) => state.docsVerification.documentsStep);

    const { isHasProofAddress, isHasLiveness, isHasPersonalData, isHasPoI } = useSelectedChecks();
    const { pageTitle } = usePageTitle();
    const isTwoSideDocument = !!documentsFiles.filter((item) => item.isTwoSideDocument).length;

    const userRole = useSelector(
        (state: RootState) => state.dashboard?.session?.organizationInfo?.organization_role,
    ) as OrganizationRoleEnum;
    const isDS = useSelector((state: RootState) => state.domain?.settings?.is_ds);

    const [errorProceedText, setErrorProceedText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { logger } = useLogger();

    const handleProceedVerificationClick = useCallback(
        async (isFailedVerification?: boolean) => {
            if (isFailedVerification) {
                dispatch(
                    docsVerificationSlice.actions.setCurrentStep(DocsVerificationStepEnum.FINISH),
                );

                return false;
            }
            if (isLoading) {
                return false;
            }
            setErrorProceedText('');
            setIsLoading(true);
            let isSuccess = true;

            try {
                try {
                    if (sdkOptions && !sdkOptions.isDemo) {
                        await proceedVerificationSDK({
                            id: sdkOptions.id,
                            url: sdkOptions.apiUrl,
                        }).unwrap();
                    }
                    dispatch(
                        docsVerificationSlice.actions.setCurrentStep(
                            DocsVerificationStepEnum.FINISH,
                        ),
                    );
                } catch (e) {
                    isSuccess = false;
                    await logger.error({
                        message: 'Error during proceedVerificationClick',
                        data: e,
                    });
                    setErrorProceedText(e?.data?.message);
                }
            } catch (err) {
                isSuccess = false;
                await logger.error({ message: 'Error during proceedVerificationClick', data: err });
            } finally {
                setIsLoading(false);
            }

            return isSuccess;
        },
        [logger, dispatch, proceedVerificationSDK, sdkOptions, isLoading],
    );

    const handleFinishClick = useCallback(
        async (isFailedVerification?: boolean) =>
            handleProceedVerificationClick(isFailedVerification),
        [handleProceedVerificationClick],
    );

    const handleBiometricsSubmit = useCallback(
        async (isFailedVerification?: boolean) => {
            if (isHasProofAddress) {
                dispatch(docsVerificationSlice.actions.goNextStep());

                return true;
            }

            return handleFinishClick(isFailedVerification);
        },
        [isHasProofAddress, handleFinishClick, dispatch],
    );

    const handleSkipClick = () => {
        dispatch(docsVerificationSlice.actions.goNextStep(true));
    };

    const handlePersonalDataSubmit = useCallback(() => {
        dispatch(docsVerificationSlice.actions.goNextStep());
    }, [dispatch]);

    const handleDocumentsSubmit = useCallback(async () => {
        if (isHasProofAddress || isHasLiveness) {
            dispatch(docsVerificationSlice.actions.goNextStep());
        } else {
            await handleFinishClick();
        }
    }, [dispatch, handleFinishClick, isHasProofAddress, isHasLiveness]);

    const handleBackClick = () => {
        if (currentTab === DocsVerificationTabEnum.DOCUMENT) {
            if (isShowRequirements) {
                dispatch(docsVerificationSlice.actions.setIsShowRequirements(false));
            } else {
                if (documentsStep === DocumentsStepEnum.SELECT_COUNTRY) {
                    dispatch(
                        docsVerificationSlice.actions.setDocumentsStep(
                            isTwoSideDocument
                                ? DocumentsStepEnum.UPLOAD_SECOND_SIDE
                                : DocumentsStepEnum.UPLOAD_FIRST_SIDE,
                        ),
                    );

                    return;
                }

                const lastFile = documentsFiles[documentsFiles.length - 1];

                if (lastFile?.id) {
                    dispatch(docsVerificationSlice.actions.deleteFileById(lastFile.id));
                }

                if (documentsFiles.length === 1) {
                    dispatch(
                        docsVerificationSlice.actions.setFileUploadApproach(
                            DocsVerificationFileUploadApproachEnum.PHOTO_AND_FILE,
                        ),
                    );
                }

                if (documentsFiles.length === 2) {
                    dispatch(
                        docsVerificationSlice.actions.setDocumentsStep(
                            DocumentsStepEnum.UPLOAD_FIRST_SIDE,
                        ),
                    );
                }

                if (!lastFile) {
                    dispatch(docsVerificationSlice.actions.goBackStep());
                }
            }
        } else {
            dispatch(docsVerificationSlice.actions.goBackStep());
        }
    };

    return (
        <TabContainer className='tabs-container' isCameraMode={isCameraMode}>
            <div id={STEPS_MOBILE_TITLE} />
            {!isCameraMode && (
                <React.Fragment>
                    {!matchesMobileSm && !isShowRequirements && (
                        <div className='header-container'>
                            <LanguageSelector className='language-selector' />
                            <div className='header-wrapper'>
                                <Icon
                                    icon={
                                        <MiniArrowIcon
                                            className='arrow-icon'
                                            width={18}
                                            height={14}
                                        />
                                    }
                                    $clickable={true}
                                    onClick={handleBackClick}
                                />
                                <Typography
                                    tag='h2'
                                    variant='h2'
                                    className='steps-title'
                                    dangerouslySetInnerHTML={{ __html: pageTitle }}
                                />
                                <div />
                            </div>
                            <Timer isHidden={true} className='timer' />
                        </div>
                    )}
                    {!isShowRequirements && (
                        <SteppedTabs
                            value={currentTab}
                            aria-label='docs-verification'
                            className='tabs-container'
                        >
                            {isHasPersonalData && (
                                <SteppedTabs.Tab
                                    label={
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: t('docVerificationWidget.tab.personalData'),
                                            }}
                                        />
                                    }
                                    value={DocsVerificationTabEnum.PERSONAL_DATA}
                                    isSuccess={
                                        currentTab === DocsVerificationTabEnum.DOCUMENT ||
                                        currentTab === DocsVerificationTabEnum.BIOMETRICS ||
                                        currentTab === DocsVerificationTabEnum.ADDRESS
                                    }
                                    isCurrent={currentTab === DocsVerificationTabEnum.PERSONAL_DATA}
                                    iconPosition='start'
                                />
                            )}
                            {isHasPoI && (
                                <SteppedTabs.Tab
                                    label={
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: t('docVerificationWidget.tab.document'),
                                            }}
                                        />
                                    }
                                    value={DocsVerificationTabEnum.DOCUMENT}
                                    isSuccess={
                                        currentTab === DocsVerificationTabEnum.BIOMETRICS ||
                                        currentTab === DocsVerificationTabEnum.ADDRESS
                                    }
                                    isCurrent={currentTab === DocsVerificationTabEnum.DOCUMENT}
                                    iconPosition='start'
                                />
                            )}
                            {isHasLiveness && (
                                <SteppedTabs.Tab
                                    label={
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: t('docVerificationWidget.tab.biometrics'),
                                            }}
                                        />
                                    }
                                    isSuccess={currentTab === DocsVerificationTabEnum.ADDRESS}
                                    isCurrent={currentTab === DocsVerificationTabEnum.BIOMETRICS}
                                    value={DocsVerificationTabEnum.BIOMETRICS}
                                />
                            )}
                            {isHasProofAddress && (
                                <SteppedTabs.Tab
                                    label={
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: t('docVerificationWidget.tab.address'),
                                            }}
                                        />
                                    }
                                    isCurrent={currentTab === DocsVerificationTabEnum.ADDRESS}
                                    value={DocsVerificationTabEnum.ADDRESS}
                                />
                            )}
                        </SteppedTabs>
                    )}
                </React.Fragment>
            )}
            {isHasPersonalData && (
                <SteppedTabs.TabPanel
                    value={currentTab}
                    name={DocsVerificationTabEnum.PERSONAL_DATA}
                >
                    <PersonalData onSubmit={handlePersonalDataSubmit} />
                </SteppedTabs.TabPanel>
            )}

            {isHasPoI && (
                <SteppedTabs.TabPanel value={currentTab} name={DocsVerificationTabEnum.DOCUMENT}>
                    <Documents
                        onSubmit={handleDocumentsSubmit}
                        isSubmitting={isLoading}
                        onBack={handleBackClick}
                    />
                </SteppedTabs.TabPanel>
            )}

            {isHasLiveness && (
                <SteppedTabs.TabPanel value={currentTab} name={DocsVerificationTabEnum.BIOMETRICS}>
                    <Selfie onSubmit={handleBiometricsSubmit} isLoading={isLoading} />
                </SteppedTabs.TabPanel>
            )}
            {isHasProofAddress && (
                <SteppedTabs.TabPanel value={currentTab} name={DocsVerificationTabEnum.ADDRESS}>
                    <Address isLoading={isLoading} onSubmit={handleFinishClick} />
                </SteppedTabs.TabPanel>
            )}
            {errorProceedText && (
                <Badge
                    text={errorProceedText}
                    status={BadgeStatusEnum.ERROR}
                    leftAddon={<ErrorMarkIcon />}
                    className='tab-badge'
                />
            )}
            {!!getEnvVar('REACT_APP_IS_SDK_DEBUG') && (
                <Button
                    variant='secondary'
                    onClick={handleSkipClick}
                    size='xl'
                    className='skip-button'
                >
                    {t('common.skip')}
                </Button>
            )}
            {(userRole === OrganizationRoleEnum.ADMIN || userRole === OrganizationRoleEnum.OWNER) &&
                isDS && <DemoDataButton />}
        </TabContainer>
    );
};
